/**
 * <ZoomPanelHeader>
 *
 * §1 of <ZoomPanel> (`docs/ui-designs/06-telehealth-and-scribe.md`). Renders:
 *   - `LIVE` red-dot label + ticking `MM:SS` timer.
 *   - State-aware Scribe button:
 *       idle      → grey "AI Scribe"
 *       streaming → green "Scribe On" with pulsing white dot
 *       paused    → amber "Paused"
 *     Clicking dispatches start / pause / resume via the props.
 *   - Red `End` button that surfaces the end-session prompt or just ends.
 *
 * State drivers live above (useScribeSession, useCallSession). This component
 * is purely presentational and gets every input through props.
 *
 * Phase 1 (rip-out) — removed the identity-verification gate (LD-4). The
 * Start button enables as soon as the session is joined.
 *
 * Narrow-width behavior
 *   The parent <ZoomPanel> is drag-resizable (240–700px). Below COMPACT_WIDTH
 *   the header collapses the Scribe button to an icon-only pill (record dot
 *   when streaming, mic when idle, pause icon when paused) so the row doesn't
 *   wrap or squash the End button. All flex items keep `flex-nowrap` +
 *   `flex-shrink-0` to guarantee a single-line layout at every width.
 */
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconMicrophone,
  IconPlayerPause,
} from '@tabler/icons-react'
import type { ScribeState, PatientPresence } from '@/components/session/callSessionReducer'
import { scribeText } from './scribeText'

/** Below this panel width, collapse the Scribe button to an icon-only pill. */
const COMPACT_WIDTH = 320

// Neutral idle bevel — reused by `finalizing`, which isn't interactive.
const IDLE_SCRIBE_STYLE = {
  className:
    'bg-white/[0.08] text-slate-300 hover:bg-white/[0.14] hover:text-white focus-visible:ring-white/40',
  shadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 1px 2px rgba(0,0,0,0.25)',
} as const

// Scribe button fill + glow, keyed on state. Streaming/paused add a soft
// colored glow; idle/finalizing keep only the neutral bevel.
const SCRIBE_STATE_STYLE: Record<ScribeState, { className: string; shadow: string }> = {
  streaming: {
    className: 'bg-[#22c55e] text-white hover:bg-[#1fb457] focus-visible:ring-emerald-300/70',
    shadow:
      'inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(0,0,0,0.3), 0 0 14px rgba(34,197,94,0.4)',
  },
  paused: {
    className: 'bg-[#f59e0b] text-white hover:bg-[#e08c07] focus-visible:ring-amber-300/70',
    shadow:
      'inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(0,0,0,0.3), 0 0 12px rgba(245,158,11,0.32)',
  },
  idle: IDLE_SCRIBE_STYLE,
  finalizing: IDLE_SCRIBE_STYLE,
}

export type ZoomPanelHeaderProps = {
  /** Current panel width in px — drives compact-mode collapse. */
  width: number
  /** Elapsed call time in seconds — formatted to `MM:SS` (tabular nums). */
  elapsedSeconds: number
  /** Current scribe lifecycle state from useCallSession. */
  scribeState: ScribeState
  /** Patient presence — gates LIVE vs "Waiting for Patient" (PD-1067). */
  patientPresence: PatientPresence
  /** True while the start mutation is in flight. */
  isStarting: boolean
  /** Click handler for the scribe state-button. */
  onScribeAction: () => void
  /** Click handler for the End button. */
  onEnd: () => void
  /** Whether the panel is in "expanded" (wide) view. Drives the toggle icon. */
  isExpanded: boolean
  /** Click handler for the expand/collapse toggle. */
  onToggleExpand: () => void
  /** Hide the scribe state-button (managers never operate the scribe). */
  hideScribe?: boolean
}

function formatMmSs(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

export function ZoomPanelHeader({
  width,
  elapsedSeconds,
  scribeState,
  patientPresence,
  isStarting,
  onScribeAction,
  onEnd,
  isExpanded,
  onToggleExpand,
  hideScribe = false,
}: ZoomPanelHeaderProps) {
  const isStreaming = scribeState === 'streaming'
  const isPaused = scribeState === 'paused'
  const isIdle = scribeState === 'idle'
  const isCompact = width < COMPACT_WIDTH

  // Don't call the visit LIVE until the patient is actually in the room —
  // amber steady dot + "Waiting for Patient" until then (PD-1067 item 4). The
  // rail is a tight flex-nowrap row, so it uses a terse "Waiting" while the
  // roomy expanded header shows the full phrase (the full waiting message also
  // renders on the video frame).
  const isLive = patientPresence === 'connected'
  const liveDotClass = isLive ? 'animate-pulse bg-[#ef4444]' : 'bg-[#f59e0b]'
  const liveLabelFull = isLive ? 'LIVE' : 'Waiting for Patient'
  const liveLabelRail = isLive ? 'LIVE' : 'Waiting'

  // Fullscreen: slim LIVE+timer strip — the controls row hosts the scribe
  // pill, End, and the collapse toggle, so the video gets the height back.
  if (isExpanded) {
    return (
      <header
        data-testid="zoom-panel-header"
        className="flex shrink-0 items-center justify-between border-b border-white/[0.07] px-[14px] py-[5px]"
      >
        <div className="flex items-center gap-[7px]">
          <span aria-hidden="true" className={`h-[8px] w-[8px] rounded-full ${liveDotClass}`} />
          <span
            className={`${scribeText.meta} font-semibold tracking-[0.06em] whitespace-nowrap text-slate-400`}
          >
            {liveLabelFull}
          </span>
        </div>
        <span data-testid="zoom-panel-timer" className={`${scribeText.timer} text-slate-100`}>
          {formatMmSs(elapsedSeconds)}
        </span>
      </header>
    )
  }

  const scribeLabel = isStreaming ? 'Scribe On' : isPaused ? 'Paused' : 'AI Scribe'

  // All three header buttons must share this base: 28px height, rounded-lg,
  // hover lift, focus-visible ring.
  const controlBase =
    'flex h-[28px] flex-shrink-0 items-center justify-center rounded-lg border-0 transition-[transform,background-color,box-shadow,color] duration-150 ease-out hover:-translate-y-px active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 motion-reduce:transform-none motion-reduce:transition-none'

  const { className: scribeStateClass, shadow: scribeShadow } = SCRIBE_STATE_STYLE[scribeState]
  const scribeAriaLabel = isStreaming
    ? 'Scribe active — pause'
    : isPaused
      ? 'Scribe paused — resume'
      : 'Start AI scribe'

  // While streaming or paused, the button toggles pause/resume and is always
  // enabled. Idle disables only while a start mutation is in flight.
  const scribeDisabled = isIdle && isStarting

  // Tighten internal spacing at compact widths so the row breathes.
  const rowGap = isCompact ? 'gap-1' : 'gap-[8px]'
  const padding = isCompact ? 'px-[10px]' : 'px-[14px]'

  return (
    <header
      data-testid="zoom-panel-header"
      data-compact={isCompact ? 'true' : 'false'}
      className={`flex shrink-0 flex-nowrap items-center justify-between border-b border-white/[0.07] py-[10px] ${padding} ${rowGap}`}
    >
      <div className="flex min-w-0 flex-shrink-0 flex-nowrap items-center gap-[7px]">
        <span
          aria-hidden="true"
          className={`h-[8px] w-[8px] flex-shrink-0 rounded-full ${liveDotClass}`}
        />
        <span
          className={`flex-shrink-0 ${scribeText.meta} font-semibold tracking-[0.06em] whitespace-nowrap text-slate-400`}
        >
          {liveLabelRail}
        </span>
      </div>
      <span
        data-testid="zoom-panel-timer"
        className={`min-w-0 flex-shrink-0 ${scribeText.timer} whitespace-nowrap text-slate-100`}
      >
        {formatMmSs(elapsedSeconds)}
      </span>
      <div className={`flex min-w-0 flex-shrink-0 flex-nowrap items-center ${rowGap}`}>
        {/* Expand/collapse toggle — window-control style icon button placed
            BEFORE the Scribe/End cluster. Click flips the panel between the
            default narrow rail (272px) and an expanded wide view (~55% of
            viewport). Sticks at the same horizontal position regardless of
            compact mode so the doctor can hit it without thinking. */}
        <button
          type="button"
          onClick={onToggleExpand}
          data-testid="zoom-panel-expand-toggle"
          data-expanded={isExpanded ? 'true' : 'false'}
          aria-label={isExpanded ? 'Collapse call view' : 'Expand call view'}
          title={isExpanded ? 'Collapse call view' : 'Expand call view'}
          className={`${controlBase} w-[28px] bg-white/[0.06] text-slate-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_1px_2px_rgba(0,0,0,0.25)] hover:bg-white/[0.12] hover:text-white focus-visible:ring-white/40`}
        >
          {isExpanded ? (
            <IconArrowsMinimize size={14} stroke={2} aria-hidden="true" />
          ) : (
            <IconArrowsMaximize size={14} stroke={2} aria-hidden="true" />
          )}
        </button>
        {!hideScribe && (
          <button
            type="button"
            onClick={onScribeAction}
            disabled={scribeDisabled}
            data-testid="zoom-panel-scribe-toggle"
            data-scribe-state={scribeState}
            aria-label={isCompact ? scribeAriaLabel : undefined}
            title={isCompact ? scribeLabel : undefined}
            className={`${controlBase} gap-[5px] ${scribeText.label} whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 ${scribeStateClass} ${
              isCompact ? 'w-[28px]' : 'px-[12px]'
            }`}
            style={{ boxShadow: scribeShadow }}
          >
            {isCompact ? (
              isStreaming ? (
                <span
                  aria-hidden="true"
                  className="inline-block h-[8px] w-[8px] flex-shrink-0 animate-pulse rounded-full bg-white"
                />
              ) : isPaused ? (
                <IconPlayerPause size={12} stroke={2.5} aria-hidden="true" />
              ) : (
                <IconMicrophone size={12} stroke={2.5} aria-hidden="true" />
              )
            ) : (
              <>
                {isStreaming && (
                  <span
                    aria-hidden="true"
                    className="inline-block h-[6px] w-[6px] flex-shrink-0 rounded-full bg-white"
                  />
                )}
                {scribeLabel}
              </>
            )}
          </button>
        )}
        <button
          type="button"
          onClick={onEnd}
          data-testid="zoom-panel-end"
          className={`${controlBase} bg-[#ef4444] ${scribeText.label} whitespace-nowrap text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.15),0_1px_2px_rgba(0,0,0,0.3)] hover:bg-[#dc2626] focus-visible:ring-red-300/70 ${
            isCompact ? 'px-[10px]' : 'px-[14px]'
          }`}
        >
          End
        </button>
      </div>
    </header>
  )
}
